/**
 * Guest-device shopper lists: the wishlist (F-20) and recently viewed (F-22).
 *
 * Both are stored in the browser only. That is a deliberate, honest scope, not
 * a shortcut to hide: nothing here syncs across devices, browsers or accounts,
 * and the wishlist dialog says so. Cross-device saving needs customer accounts
 * or an app, which is a separate decision.
 *
 * Privacy: only product handles are stored — no name, email, cart or customer
 * data — and nothing is sent anywhere. Product details are fetched fresh from
 * the store's public /products/<handle>.js whenever a list is shown, so a
 * saved item always shows its current price and availability, and one that has
 * been deleted or unpublished simply drops off.
 *
 * Storage can fail (private browsing, blocked cookies, a full quota). Every
 * read and write is guarded; on failure the lists keep working for the current
 * visit in memory and the UI says they will not be remembered.
 */

(() => {
  if (window.SquishLists) return;

  /* ------------------------------------------------------------- money */

  /**
   * Formats cents the way snippets/squish-money does in Liquid: ₹ plus the
   * amount, with ".00" dropped on a whole rupee and genuine paise kept
   * (₹499, ₹559.20). Uses window.squishMoney.symbol, written by
   * layout/theme.liquid; falls back to the store's own format.
   */
  // `whole` rounds a selling price to the nearest rupee for display, when the
  // theme setting asks for it — the same rule as snippets/squish-money.
  const formatMoney = (cents, { whole: roundWhole = false } = {}) => {
    const cfg = window.squishMoney || {};
    const value = roundWhole && cfg.wholePrices ? Math.round(Number(cents) / 100) : Number(cents) / 100;
    const whole = Number.isInteger(value) && Boolean(cfg.symbol);
    const fixed = (n, d, thousands, decimal) => {
      const [int, frac] = n.toFixed(whole ? 0 : d).split('.');
      const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? grouped + decimal + frac : grouped;
    };
    if (cfg.symbol) return `${cfg.symbol}${fixed(value, 2, ',', '.')}`;
    const format = cfg.format;
    if (typeof format === 'string' && format.includes('{{')) {
      return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, kind) => {
        switch (kind) {
          case 'amount': return fixed(value, 2, ',', '.');
          case 'amount_no_decimals': return fixed(value, 0, ',', '.');
          case 'amount_with_comma_separator': return fixed(value, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return fixed(value, 0, '.', ',');
          case 'amount_with_apostrophe_separator': return fixed(value, 2, "'", '.');
          case 'amount_no_decimals_with_space_separator': return fixed(value, 0, ' ', '.');
          case 'amount_with_space_separator': return fixed(value, 2, ' ', ',');
          default: return fixed(value, 2, ',', '.');
        }
      });
    }
    try {
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: cfg.currency || 'INR' }).format(value);
    } catch {
      return String(value);
    }
  };

  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  /* ----------------------------------------------------- guarded storage */

  const HANDLE = /^[^\s/?#\\]{1,255}$/;

  class SafeList {
    constructor(key, max) {
      this.key = key;
      this.max = max;
      this.memory = [];
      this.persistent = this.probe();
    }

    probe() {
      try {
        const probe = '__squish_probe__';
        window.localStorage.setItem(probe, '1');
        window.localStorage.removeItem(probe);
        return true;
      } catch {
        return false;
      }
    }

    read() {
      if (!this.persistent) return [...this.memory];
      try {
        const parsed = JSON.parse(window.localStorage.getItem(this.key) || '[]');
        return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string' && HANDLE.test(h)).slice(0, this.max) : [];
      } catch {
        // Corrupt JSON: treat as empty rather than breaking every page.
        return [];
      }
    }

    write(list) {
      this.memory = list;
      if (!this.persistent) return;
      try {
        window.localStorage.setItem(this.key, JSON.stringify(list));
      } catch {
        this.persistent = false;
      }
    }

    has(handle) {
      return this.read().includes(handle);
    }

    add(handle) {
      if (!HANDLE.test(handle)) return;
      this.write([handle, ...this.read().filter((h) => h !== handle)].slice(0, this.max));
    }

    remove(handle) {
      this.write(this.read().filter((h) => h !== handle));
    }
  }

  const wishlist = new SafeList('squish:wishlist:v1', 100);
  const recent = new SafeList('squish:recent:v1', 12);

  const CHANGE = 'squish:wishlist-change';
  const announce = (message) => {
    let live = document.getElementById('SquishListsLive');
    if (!live) {
      live = document.createElement('div');
      live.id = 'SquishListsLive';
      live.className = 'visually-hidden';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      document.body.appendChild(live);
    }
    // Clearing first makes a repeated identical message announce again.
    live.textContent = '';
    setTimeout(() => (live.textContent = message), 30);
  };

  const notifyChange = () => window.dispatchEvent(new CustomEvent(CHANGE));
  window.addEventListener('storage', (e) => {
    if (e.key === wishlist.key) notifyChange();
  });

  /* --------------------------------------------------- product data + cards */

  const productCache = new Map();

  /** Resolves a product, `null` if it no longer exists, or throws on a network failure. */
  async function fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    const root = window.Shopify?.routes?.root || '/';
    const res = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
    if (res.status === 404) {
      productCache.set(handle, null);
      return null;
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const product = await res.json();
    productCache.set(handle, product);
    return product;
  }

  const imageUrl = (src, width) => {
    try {
      const url = new URL(src, window.location.href);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch {
      return src;
    }
  };

  /**
   * The two-part heading, by the rules in snippets/squish-product-title.liquid
   * (2–4; the custom.subtitle metafield is not in the product JSON): split
   * before the configured word ("Graphic"), else at the first " - ".
   */
  const splitTitle = (title) => {
    const word = (window.squishTitleSplit || '').trim();
    const marker = word ? ` ${word} ` : '';
    if (marker && title.includes(marker)) {
      const name = title.split(marker)[0].trim();
      return [name, title.slice(name.length).trim()];
    }
    if (title.includes(' - ')) {
      const name = title.split(' - ')[0].trim();
      return [name, title.slice(title.indexOf(' - ') + 3).trim()];
    }
    return [title, ''];
  };

  /**
   * The variant a card prices, as in snippets/squish-percent-off.liquid: the
   * lowest-priced one, preferring one in stock, so price, MRP and percentage
   * all belong to the same variant.
   */
  const pricedVariant = (product) => {
    const variants = product.variants || [];
    const cheapest = variants.filter((v) => v.price === product.price);
    return cheapest.find((v) => v.available) || cheapest[0] || variants[0] || null;
  };

  /** Whole-number percentage off, or 0 when there is no genuine discount. */
  const percentOff = (variant) => {
    if (!variant || !(variant.compare_at_price > variant.price)) return 0;
    return Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
  };

  /** Same markup as snippets/squish-product-card.liquid, so both look identical. */
  function cardHtml(product, { removable = false } = {}) {
    const variant = pricedVariant(product);
    const off = percentOff(variant);
    const price = variant ? variant.price : product.price;
    const badge = !product.available ? 'Sold out' : off > 0 ? `${off}% OFF` : '';
    const [name, sub] = splitTitle(product.title);
    const image = product.featured_image
      ? `<img src="${escapeHtml(imageUrl(product.featured_image, 600))}" alt="${escapeHtml(product.title)}" width="600" height="750" loading="lazy">`
      : `<svg viewBox="0 0 120 120" style="--top: #9DD155" aria-hidden="true"><use href="#sq-tee"/></svg>`;
    const priceHtml = product.available
      ? `<div class="sq-price sq-price--card">
          <span class="sq-price__now">${off > 0 ? '<span class="visually-hidden">Sale price </span>' : ''}${formatMoney(price, { whole: true })}</span>
          ${
            off > 0
              ? `<span class="sq-price__mrp"><span aria-hidden="true">MRP </span><s><span class="visually-hidden">MRP, regular price </span>${formatMoney(variant.compare_at_price)}</s></span>`
              : ''
          }
        </div>`
      : '<span class="soldout">Sold out</span>';

    return `
      <article class="sq-pc">
        <a class="hit" href="${escapeHtml(product.url)}">
          <div class="im" style="background: var(--sq-tile, #EAF4D3)">
            ${badge ? `<span class="bd${off > 0 && product.available ? ' bd--off' : ''}">${badge}</span>` : ''}
            ${image}
          </div>
          <h3 class="sq-pc__title"><span class="sq-pc__title__name">${escapeHtml(name)}</span>${
            sub ? ` <span class="sq-pc__title__sub">${escapeHtml(sub)}</span>` : ''
          }</h3>
          <div class="pr">${priceHtml}</div>
        </a>
        ${
          removable
            ? `<button type="button" class="sq-pc__remove" data-remove="${escapeHtml(product.handle)}" aria-label="Remove ${escapeHtml(product.title)} from wishlist">Remove</button>`
            : `<squish-wishlist-button data-handle="${escapeHtml(product.handle)}" data-title="${escapeHtml(product.title)}"></squish-wishlist-button>`
        }
      </article>`;
  }

  /** Loads many products at once. 404s are reported separately from network errors. */
  async function loadProducts(handles) {
    const results = await Promise.allSettled(handles.map((h) => fetchProduct(h)));
    const products = [];
    const gone = [];
    let failed = 0;
    results.forEach((r, i) => {
      if (r.status === 'rejected') failed++;
      else if (r.value === null) gone.push(handles[i]);
      else products.push(r.value);
    });
    return { products, gone, failed };
  }

  /* ------------------------------------------------------ wishlist button */

  class SquishWishlistButton extends HTMLElement {
    connectedCallback() {
      this.handle = this.dataset.handle;
      this.title_ = this.dataset.title || 'this item';
      if (!this.querySelector('button')) {
        // The heart path is inlined rather than pulled from the sprite with
        // <use>: CSS cannot reach a <use>-cloned path (it lives in a shadow
        // tree), so the saved-state fill rule would never apply. Inline, the
        // `.sq-heart[aria-pressed='true'] svg path` fill works directly.
        this.innerHTML = `
          <button type="button" class="sq-heart" aria-pressed="false">
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M12 20s-7-4.5-8.5-9A4.5 4.5 0 0 1 12 7a4.5 4.5 0 0 1 8.5 4C19 15.5 12 20 12 20z" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>`;
      }
      this.button = this.querySelector('button');
      this.button.addEventListener('click', (e) => {
        // Cards are links; a heart must never navigate.
        e.preventDefault();
        e.stopPropagation();
        this.toggle();
      });
      this.onChange = () => this.sync();
      window.addEventListener(CHANGE, this.onChange);
      this.sync();
    }

    disconnectedCallback() {
      window.removeEventListener(CHANGE, this.onChange);
    }

    sync() {
      const saved = wishlist.has(this.handle);
      this.button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      this.button.setAttribute('aria-label', `${saved ? 'Remove' : 'Save'} ${this.title_} ${saved ? 'from' : 'to'} wishlist`);
    }

    toggle() {
      const saved = wishlist.has(this.handle);
      if (saved) wishlist.remove(this.handle);
      else wishlist.add(this.handle);
      notifyChange();
      announce(saved ? `${this.title_} removed from your wishlist.` : `${this.title_} saved to your wishlist.`);
    }
  }
  customElements.define('squish-wishlist-button', SquishWishlistButton);

  /* ------------------------------------------------------- header count */

  class SquishWishlistCount extends HTMLElement {
    connectedCallback() {
      this.onChange = () => this.render();
      window.addEventListener(CHANGE, this.onChange);
      this.render();
    }
    disconnectedCallback() {
      window.removeEventListener(CHANGE, this.onChange);
    }
    render() {
      const n = wishlist.read().length;
      this.textContent = n > 99 ? '99+' : String(n);
      this.hidden = n === 0;
    }
  }
  customElements.define('squish-wishlist-count', SquishWishlistCount);

  /* ---------------------------------------------------- wishlist dialog */

  class SquishWishlistDialog extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.body = this.querySelector('[data-body]');
      this.note = this.querySelector('[data-note]');

      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-sq-wishlist-open]');
        if (opener) {
          e.preventDefault();
          this.open(opener);
        }
      });

      this.querySelector('[data-close]')?.addEventListener('click', () => this.dialog.close());
      // A click on the backdrop lands on the <dialog> element itself.
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => {
        this.opener?.focus?.();
      });

      this.body.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-remove]');
        if (!remove) return;
        const handle = remove.dataset.remove;
        const title = remove.closest('.sq-pc')?.querySelector('h3')?.textContent?.trim() || 'Item';
        wishlist.remove(handle);
        notifyChange();
        announce(`${title} removed from your wishlist.`);
        this.render();
      });

      window.addEventListener(CHANGE, () => {
        if (this.dialog.open) this.render();
      });
    }

    open(opener) {
      this.opener = opener;
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
      this.render();
    }

    async render() {
      const handles = wishlist.read();

      this.note.textContent = wishlist.persistent
        ? 'Saved on this device only. Clearing your browser data, or opening the store on another device, starts a fresh list.'
        : "Your browser is not letting this store save anything, so this list lasts only until you leave the page.";

      if (handles.length === 0) {
        this.body.innerHTML = `
          <div class="sq-wl__empty">
            <p class="sq-wl__empty-title">Nothing saved yet</p>
            <p>Tap the heart on any piece to keep it here.</p>
          </div>`;
        return;
      }

      this.body.innerHTML = '<p class="sq-wl__loading" role="status">Loading your saved pieces…</p>';
      const { products, gone, failed } = await loadProducts(handles);

      // Anything the store no longer has is dropped from the saved list for
      // good. A network failure is not the same thing, so those stay.
      if (gone.length) {
        gone.forEach((h) => wishlist.remove(h));
        notifyChange();
      }

      // The list may have been edited while we were loading.
      if (!this.dialog.open) return;

      if (products.length === 0) {
        this.body.innerHTML = `
          <div class="sq-wl__empty">
            <p class="sq-wl__empty-title">${failed ? "Couldn't load your saved pieces" : 'Nothing saved yet'}</p>
            <p>${failed ? 'Check your connection and open the wishlist again.' : 'The pieces you saved are no longer in the store.'}</p>
          </div>`;
        return;
      }

      this.body.innerHTML = `
        ${failed ? '<p class="sq-wl__warn" role="status">Some saved pieces could not be loaded just now.</p>' : ''}
        <div class="sq-wl__grid">${products.map((p) => cardHtml(p, { removable: true })).join('')}</div>`;
    }
  }
  customElements.define('squish-wishlist-dialog', SquishWishlistDialog);

  /* ------------------------------------------------------ recently viewed */

  /** Records the product page being viewed. Placed once on the product template. */
  class SquishViewRecorder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.handle) recent.add(this.dataset.handle);
    }
  }
  customElements.define('squish-view-recorder', SquishViewRecorder);

  class SquishRecentlyViewed extends HTMLElement {
    async connectedCallback() {
      const limit = Number(this.dataset.limit) || 4;
      const exclude = this.dataset.exclude;
      const handles = recent.read().filter((h) => h !== exclude);
      if (handles.length === 0) return;

      const { products, gone } = await loadProducts(handles.slice(0, limit + 4));
      gone.forEach((h) => recent.remove(h));

      // Show only what still exists, and only what is for sale to look at; the
      // section stays hidden when nothing valid is left.
      const shown = products.slice(0, limit);
      if (shown.length === 0) return;

      const grid = this.querySelector('[data-grid]');
      grid.innerHTML = shown.map((p) => cardHtml(p)).join('');
      this.hidden = false;
    }
  }
  customElements.define('squish-recently-viewed', SquishRecentlyViewed);

  window.SquishLists = { wishlist, recent, formatMoney, escapeHtml };
  window.SquishMoney = { format: formatMoney };
})();
